import { Box, TextField } from "@mui/material"
import type { Row } from "@tanstack/react-table"
import type { ChangeEvent, FC, ReactElement } from "react"
import { useEffect, useRef, useState } from "react"

import { useOrderQuantityColumn } from "../hooks/useOrderQuantityColumn"

import { FIELD_LABEL_CLASSES } from "@/constants/forms"
import type { OrderLine } from "@/graphql/codegen/graphql"
import { usePurchaseOrderReadonly } from "@/screens/Procurement/PurchaseOrder/hooks/usePurchaseOrderReadonly"
import { QuantityField } from "@/screens/Procurement/PurchaseOrder/ProductAdded/components/QuantityField"
import { formatNumericInput } from "@/utils/number"
import { parseNumberOfPallets } from "@/utils/purchaseOrder"

export const OrderQuantityColumn: FC<{ row: Row<OrderLine> }> = ({ row }): ReactElement => {
  const { uomsDropdownList, handleInputChange, getLineValue } = useOrderQuantityColumn({ row })
  const { isReadonly } = usePurchaseOrderReadonly()
  const product = row.original.product
  const isUserInputRef = useRef(false)

  const uomId = getLineValue("uomId")
  const numberOfPallets = getLineValue("numberOfPallets")
  const quantityInUom = getLineValue("quantityInUom")

  const [numberOfPalletsInputValue, setNumberOfPalletsInputValue] = useState<string | number>(numberOfPallets)

  useEffect(() => {
    const parsedValue = parseNumberOfPallets(numberOfPallets, isUserInputRef.current)

    setNumberOfPalletsInputValue(parsedValue)
  }, [numberOfPallets])

  const handlePalletsInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const formattedValue = formatNumericInput(value)

    setNumberOfPalletsInputValue(formattedValue)
    handleInputChange({ value: String(formattedValue), name: "numberOfPallets" })
    isUserInputRef.current = true
  }

  const handleOnFocus = () => {
    if (numberOfPalletsInputValue === 0 || numberOfPalletsInputValue === "0") {
      setNumberOfPalletsInputValue("")
    }
  }

  const handleOnBlur = () => {
    if (!numberOfPalletsInputValue && numberOfPalletsInputValue !== 0) {
      setNumberOfPalletsInputValue(0)
      handleInputChange({ value: "0", name: "numberOfPallets" })
    }
    isUserInputRef.current = false
  }

  const handleOnWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    ;(e.target as HTMLInputElement).blur()
  }

  return (
    <Box className='flex content-center gap-4'>
      <Box className={"relative w-[120px]"} data-testid='po-pallets-container'>
        <label className={FIELD_LABEL_CLASSES} htmlFor={`product-${product?.publicId}-pallets-input`}>
          Pallets
        </label>
        <TextField
          id={`product-${product?.publicId}-pallets-input`}
          value={numberOfPalletsInputValue}
          fullWidth
          disabled={isReadonly}
          onChange={handlePalletsInputChange}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onWheel={handleOnWheel}
          slotProps={{
            input: {
              type: "number",
              inputMode: "numeric",
              style: {
                MozAppearance: "textfield", // For Firefox
              },
            },
          }}
        />
      </Box>

      <QuantityField
        quantityInUom={quantityInUom}
        uomId={uomId}
        onInputChange={handleInputChange}
        uoms={uomsDropdownList}
        product={product}
        disabled={isReadonly}
      />
    </Box>
  )
}
